import React, { useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { WELCOME_KIT_ITEMS } from './welcomeKit';
import { trackWelcomeKit, WELCOME_KIT_EVENTS } from './tracking';

// web40 variation: full-screen intro shown between the loading bars and the
// pricing page. Values come from Ethan's "Welcome Kit" mockup (Sep 19 2026):
// 390px artboard, Lato, blue gradient, staggered rise-in.

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
`;

const riseIn = css<{ $delay?: number }>`
  animation: ${rise} 0.55s ease both;
  animation-delay: ${(p) => p.$delay ?? 0}s;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Screen = styled.div<{ $fadingOut?: boolean }>`
  min-height: 100vh;
  box-sizing: border-box;
  padding: 72px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(180deg, #e3eeff 0%, #f6f9ff 55%, #ffffff 100%);
  color: #2d3448;
  font-family: Lato, system-ui, sans-serif;
  opacity: ${(p) => (p.$fadingOut ? 0 : 1)};
  transition: opacity 0.45s ease;

  @media (min-width: 600px) { padding-top: 64px; }
`;

const Column = styled.div`
  width: 100%;
  max-width: 390px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Heading = styled.h1<{ $delay?: number }>`
  ${riseIn}
  margin: 18px 12px 0;
  width: 300px;
  max-width: 100%;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: #1b2236;
  text-align: center;
  text-wrap: balance;
`;

const Pill = styled.div<{ $delay?: number }>`
  ${riseIn}
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 12px;
  border-radius: 14px;
  background: #d6e4fb;
  border: 1px solid #b9cff4;
  svg { flex: none; }
`;

const PillTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  color: #1a47b8;
`;

const PillSub = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.35;
  color: #34405a;
`;

const Card = styled.ul<{ $delay?: number }>`
  ${riseIn}
  list-style: none;
  margin: 20px 0 0;
  width: 100%;
  box-sizing: border-box;
  padding: 22px 18px;
  background: #ffffff;
  border: 1px solid #e4ecfa;
  border-radius: 16px;
  box-shadow: 0 6px 22px rgba(60, 100, 180, 0.1);
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15.5px;
  line-height: 1.4;
  color: #2d3448;
  strong { color: #1b2236; }
`;

const Tile = styled.div`
  flex: none;
  width: 92px;
  height: 92px;
  box-sizing: border-box;
  padding: 6px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #dce6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  img { display: block; width: 100%; height: 100%; object-fit: contain; }
`;

const CtaWrap = styled.div<{ $delay?: number }>`
  ${riseIn}
  margin-top: auto;
  padding-top: 24px;
  width: 100%;

  @media (min-width: 600px) { margin-top: 28px; padding-top: 0; }
`;

const Cta = styled.button`
  width: 100%;
  height: 56px;
  border: 0;
  border-radius: 14px;
  background: #2563eb;
  color: #ffffff;
  font-family: Lato, system-ui, sans-serif;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);
`;

function Check() {
  return (
    <svg width="22" height="22" viewBox="0 0 20 20" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="10" fill="#2563EB" />
      <path d="M5.8 10.4 L8.6 13.1 L14.2 7.2" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function WelcomeKitIntro({ onContinue, isFadingOut }: { onContinue: () => void; isFadingOut?: boolean }) {
  useEffect(() => {
    trackWelcomeKit(WELCOME_KIT_EVENTS.introViewed);
  }, []);

  return (
    <Screen $fadingOut={isFadingOut} data-welcome-kit-intro>
      <Column>
        <Heading $delay={0}>A special gift to help you <br />kickstart your fitness journey</Heading>
        <Pill $delay={0.1}>
          <Check />
          <div>
            <PillTitle>Included in your membership</PillTitle>
            <PillSub>Ships to your door when your 14-day trial ends.</PillSub>
          </div>
        </Pill>
        <Card $delay={0.2} aria-label="Your free welcome kit">
          {WELCOME_KIT_ITEMS.map((item) => (
            <Row key={item.id}>
              <Tile><img src={item.image} alt={item.name} /></Tile>
              <div><strong>{item.intro.lead}</strong>{item.intro.rest}</div>
            </Row>
          ))}
        </Card>
        <CtaWrap $delay={0.3}>
          <Cta
            type="button"
            onClick={() => {
              trackWelcomeKit(WELCOME_KIT_EVENTS.introCtaClicked);
              onContinue();
            }}
          >
            Show me my plan
          </Cta>
        </CtaWrap>
      </Column>
    </Screen>
  );
}
